import { productsApi } from '../api/products';

/**
 * Recommendations Service - Handles business logic for product recommendations
 * Separates concerns between API calls (api/) and business logic (services/)
 */
class RecommendationsService {
  /**
   * Load recommended/trending products
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Number of products to fetch (default: 8)
   * @returns {Object} Result with success status and products
   */
  async loadRecommendedProducts(page = 1, limit = 8) {
    try {
      // Fetch all products - in the future, you can add filters for trending/popular
      const result = await productsApi.getAll(page, limit);

      if (result && result.items && Array.isArray(result.items)) {
        // Map products to ensure proper field names
        const mappedProducts = result.items.map(product => this.formatProductForDisplay(product));

        return {
          success: true,
          data: mappedProducts,
          total: result.total || mappedProducts.length,
          page: result.page || page,
          limit: result.pageSize || limit
        };
      }

      return {
        success: false,
        data: [],
        error: 'No products available',
        total: 0,
        page: 1,
        limit: limit
      };
    } catch (error) {
      console.error('Error loading recommended products:', error);
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to load recommended products',
        total: 0,
        page: 1,
        limit: limit
      };
    }
  }

  /**
   * Format product data for display
   * Ensures all required fields are present with proper names
   * @param {Object} product - Raw product data from API
   * @returns {Object} Formatted product object
   */
  formatProductForDisplay(product) {
    return {
      ...product,
      // Ensure id is set correctly from multiple possible fields
      id: product._id || product.id || product.productId,
      _id: product._id || product.id || product.productId,
      // Ensure name is set correctly
      name: product.productName || product.name || 'Product',
      productName: product.productName || product.name || 'Product',
      // Ensure price is set correctly - handle pricing array
      price: product.pricing?.[0]?.discountedPrice || product.pricing?.[0]?.normalPrice ||
             product.productPrice || product.price || 0,
      // Ensure image URL is set (strip whitespace)
      imageUrl: this.getValidImageUrl(product),
      featuredImage: this.getValidImageUrl(product),
      // Ensure category is set correctly
      category: product.productCategory || product.category || 'General',
      // Ensure description is set
      description: product.productDescription || product.description || '',
      // Ensure stock is available - handle pricing array
      stock: product.pricing?.[0]?.availableStock ||
             product.availableStock || product.stock || 0,
      // Include pricing array if available
      pricing: product.pricing || []
    };
  }

  /**
   * Extract valid image URL from multiple possible fields
   * Handles empty and whitespace-only strings
   * @param {Object} product - Product object
   * @returns {string} Valid image URL or empty string
   */
  getValidImageUrl(product) {
    const imageFields = [
      product.imageUrl,
      product.image,
      product.featuredImage,
      product.thumbailImagePath
    ];

    for (const imageUrl of imageFields) {
      if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim()) {
        return imageUrl.trim();
      }
    }

    return '';
  }
}

const recommendationsService = new RecommendationsService();
export default recommendationsService;
